import api from "@/services/api"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import { BASE_TRANSACTIONS_QUERY_KEY } from "../queryKeys"
import type {
  PaginatedResponse,
  PaginationQuery,
} from "../sharedTypes/pagination"
import type {
  TransactionStatus,
  TransactionMethod,
  TransactionType,
} from "../sharedTypes/transactions"
import type { RecursivelyConvertDatesToStrings } from "@/utils/typeUtilities"

export type ListTransactionsFilter = {
  date: string | null
}

export type ListTransactionsQuery = ListTransactionsFilter &
  PaginationQuery

export type ListTransactionsResponseItem = {
  id: string
  invoiceId: string
  type: TransactionType
  createdAt: Date
  status: TransactionStatus
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedTotal: number
  transactionMethod: TransactionMethod | null
  cardScheme: string | null
  cardNumberLast4: string | null
  currency: string
  deviceSerialNumber: string | null
  uti: string | null
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardCurrencyAmount: number
  dccCardholderCurrency: string
}

export const listTransactions = async (
  query?: ListTransactionsQuery
) => {
  const {
    body: { items, ...body },
  } = await api.get<
    PaginatedResponse<
      RecursivelyConvertDatesToStrings<ListTransactionsResponseItem>
    >
  >("/transactions/", {
    query,
  })
  return {
    items: items.map((transaction) => ({
      ...transaction,
      createdAt: DateFns.parseISO(transaction.createdAt),
    })),
    ...body,
  }
}

export const useTransactions = (
  query: MaybeRef<ListTransactionsQuery>
) =>
  useQuery(
    [BASE_TRANSACTIONS_QUERY_KEY, query] as const,
    ({ queryKey: [_name, _query] }) =>
      listTransactions(unref(_query)),
    {
      refetchInterval: 60 * 1000,
      staleTime: 0,
    }
  )
